import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { PagePreset } from '../../../../../../services/voucher-design.service';

export const MIN_SIDE_MM = 50;
export const MAX_SIDE_MM = 1050;

/** A blank page: its size (a preset or any measure within the limits) and colour. */
export interface BlankPage {
  width_mm: number;
  height_mm: number;
  landscape: boolean;
  background_color: string;
}

export const DEFAULT_BLANK_PAGE: BlankPage = {
  width_mm: 210,
  height_mm: 297,
  landscape: false,
  background_color: '#ffffff',
};

@Component({
  selector: 'app-blank-page-form',
  standalone: true,
  imports: [HlmButtonImports, HlmInputImports, HlmLabelImports, HlmTypographyImports],
  templateUrl: './blank-page-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlankPageFormComponent {
  readonly presets = input<PagePreset[]>([]);
  readonly page = model<BlankPage>(DEFAULT_BLANK_PAGE);

  readonly minSide = MIN_SIDE_MM;
  readonly maxSide = MAX_SIDE_MM;

  /** The page as it prints: the longer side across when landscape. */
  readonly printed = computed(() => {
    const p = this.page();
    const [short, long] = [p.width_mm, p.height_mm].sort((a, b) => a - b);
    return p.landscape ? { width: long, height: short } : { width: short, height: long };
  });
  readonly activePreset = computed(() => {
    const p = this.page();
    return (
      this.presets().find((x) => x.width_mm === p.width_mm && x.height_mm === p.height_mm)?.key ??
      null
    );
  });
  readonly valid = computed(() => {
    const p = this.page();
    const inRange = (v: number) => Number.isFinite(v) && v >= MIN_SIDE_MM && v <= MAX_SIDE_MM;
    return (
      inRange(p.width_mm) && inRange(p.height_mm) && /^#[0-9A-Fa-f]{6}$/.test(p.background_color)
    );
  });
  /** The preview box: at most 160 px on the longer side. */
  readonly previewStyle = computed(() => {
    const { width, height } = this.printed();
    const factor = 160 / Math.max(width, height);
    return `width:${Math.round(width * factor)}px;height:${Math.round(height * factor)}px;background:${this.page().background_color}`;
  });

  usePreset(preset: PagePreset) {
    this.page.update((p) => ({ ...p, width_mm: preset.width_mm, height_mm: preset.height_mm }));
  }

  onSide(field: 'width_mm' | 'height_mm', event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value)) this.page.update((p) => ({ ...p, [field]: value }));
  }

  setLandscape(landscape: boolean) {
    this.page.update((p) => ({ ...p, landscape }));
  }

  onColor(event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(value))
      this.page.update((p) => ({ ...p, background_color: value.toLowerCase() }));
  }
}
